import { Box, Stack, TextField, Typography } from "@mui/material";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { Check, Cross } from "akar-icons";
import { useFormik } from "formik";
import React from "react";
import { toast } from "react-toastify";
import * as yup from "yup";

import { changeCustomerInformation } from "@customer-app/state/actionCreators/auth";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerDispatch } from "@customer-app/state/typedCustomerReduxHooks";

export interface IEmailProps {
  email?: string;
}

export const EmailModal = ({ email }: IEmailProps): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const validationSchema = yup.object({
    email: yup.string().email("Enter a valid email").required("Email is required"),
  });

  const formik = useFormik({
    initialValues: {
      email,
    },
    validationSchema,
    onSubmit(values) {
      if (values.email) {
        dispatch(changeCustomerInformation({ email: values.email.toLowerCase().trim() }, { email })).catch(() =>
          toast.error(typedLocalize("settings.personalInformation.emailUpdateError"))
        );
        closeConfirmationModalForEmail();
      }
    },
  });

  const closeConfirmationModalForEmail = () => {
    closeModal();
    formik.resetForm();
  };
  return (
    <React.Fragment>
      <Stack alignItems="center" justifyContent="center" py="45px" spacing="10px">
        <React.Fragment>
          <Typography variant="h4" sx={{ marginBottom: 0 }}>
            {typedLocalize("settings.personalInformation.newEmail")}
          </Typography>
          <TextField
            data-testid="email-input"
            required
            inputProps={{ style: { textAlign: "center" } }}
            InputLabelProps={{ shrink: false }}
            InputProps={{
              startAdornment:
                formik.touched.email && !formik.errors.email ? (
                  <Check data-testid="success-check" />
                ) : formik.errors.email ? (
                  <Cross data-testid="error-cross" style={{ color: "#d32f2f" }} />
                ) : undefined,
            }}
            label="Email"
            variant="outlined"
            type="email"
            name="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            error={formik.touched.email ? Boolean(formik.errors.email) : undefined}
            helperText={formik.touched.email ? formik.errors.email : null}
            sx={{
              ".MuiInputLabel-root": {
                display: "none",
              },
              ".MuiOutlinedInput-root": {
                fontSize: "14px",
                borderRadius: "8px",
                height: "40px",
              },
              ".MuiOutlinedInput-input": {
                height: "40px",
                padding: 0,
              },
              width: "100%",
            }}
          />
        </React.Fragment>
      </Stack>
      <Box width="100%" sx={theme => ({ borderTop: `1px solid ${theme?.modernLineDividers}` })}>
        <AquariusButton filled onClick={formik.submitForm}>
          {typedLocalize("general.confirmBtn")}
        </AquariusButton>
      </Box>
    </React.Fragment>
  );
};
